<template>
  <Teleport to="body">
    <div v-if="b2bModal.open" class="b2b-overlay" @click.self="close">
      <div class="b2b-panel" role="dialog" aria-modal="true" aria-labelledby="b2b-title">
        <button class="b2b-close" type="button" aria-label="Close" @click="close">&times;</button>

        <template v-if="!sent">
          <p v-if="vehicleLabel" class="b2b-vehicle notranslate" translate="no">{{ vehicleLabel }}</p>

          <h2 id="b2b-title" class="b2b-title">Get B2B Prices</h2>
          <p class="b2b-lead">
            Dealer and export pricing for verified partners. Leave your details and we will come
            back with net B2B pricing for this vehicle, usually within 24 hours.
          </p>

          <form class="b2b-form" @submit.prevent="submit">
            <label class="b2b-field">
              <span>Company</span>
              <input ref="firstField" v-model.trim="form.company" type="text" required maxlength="255" />
            </label>

            <label class="b2b-field">
              <span>Name</span>
              <input v-model.trim="form.name" type="text" required maxlength="255" />
            </label>

            <label class="b2b-field">
              <span>WhatsApp</span>
              <input v-model.trim="form.whatsapp" type="tel" required maxlength="50" placeholder="+421 900 000 000" />
            </label>

            <label class="b2b-field">
              <span>Email</span>
              <input v-model.trim="form.email" type="email" required maxlength="255" />
            </label>

            <!-- Bots fill this in, people never see it. -->
            <input v-model="form.website" class="b2b-trap" type="text" tabindex="-1" autocomplete="off" aria-hidden="true" />

            <p v-if="error" class="b2b-error">{{ error }}</p>

            <button class="b2b-submit" type="submit" :disabled="sending">
              {{ sending ? 'Sending...' : 'Request B2B prices' }}
            </button>
          </form>
        </template>

        <div v-else class="b2b-done">
          <h2 class="b2b-title">Thank you</h2>
          <p class="b2b-lead">
            We have your details and will come back with B2B pricing shortly.
          </p>
          <button class="b2b-submit" type="button" @click="close">Close</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import axios from 'axios';
import { b2bModal } from '../b2bModal.js';

export default {
  name: 'B2BPriceModal',

  data() {
    return {
      b2bModal,
      sending: false,
      sent: false,
      error: '',
      form: { company: '', name: '', whatsapp: '', email: '', website: '' },
    };
  },

  computed: {
    vehicleLabel() {
      const v = b2bModal.vehicle;
      if (!v) return '';
      return [v.brand || v.make, v.model, v.year].filter(Boolean).join(' ');
    },
  },

  watch: {
    'b2bModal.open'(open) {
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        this.sent = false;
        this.error = '';
        this.$nextTick(() => this.$refs.firstField?.focus());
      }
    },
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },

  methods: {
    onKeydown(event) {
      if (event.key === 'Escape' && b2bModal.open) this.close();
    },

    close() {
      b2bModal.hide();
    },

    async submit() {
      this.sending = true;
      this.error = '';

      try {
        await axios.post('/api/b2b-requests', {
          ...this.form,
          vehicle_id: b2bModal.vehicle?.id ?? null,
          source: b2bModal.source,
        });

        this.sent = true;
        this.form = { company: '', name: '', whatsapp: '', email: '', website: '' };
      } catch (e) {
        this.error = e.response?.status === 429
          ? 'Too many requests. Please try again in a minute.'
          : 'Something went wrong. Please check the fields and try again.';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.b2b-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow-y: auto;
}

.b2b-panel {
  position: relative;
  width: 100%;
  max-width: 30rem;
  background: #111111;
  border: 1px solid #2c2c2f;
  padding: 2.5rem 2rem;
}

.b2b-close {
  position: absolute;
  top: 0.75rem;
  right: 1rem;
  background: none;
  border: none;
  color: #aeaeae;
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.3s ease;
}

.b2b-close:hover {
  color: #ffffff;
}

.b2b-vehicle {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #aeaeae;
}

.b2b-title {
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #ffffff;
}

.b2b-lead {
  margin: 0 0 1.75rem;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #aeaeae;
}

.b2b-field {
  display: block;
  margin-bottom: 1rem;
}

.b2b-field span {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #aeaeae;
}

.b2b-field input {
  width: 100%;
  padding: 0.75rem 0.9rem;
  background: #1a1a1c;
  border: 1px solid #2c2c2f;
  border-radius: 0;
  color: #ffffff;
  font-size: 0.9375rem;
  transition: border-color 0.3s ease;
}

.b2b-field input:focus {
  outline: none;
  border-color: #ffffff;
}

.b2b-trap {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.b2b-error {
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  color: #e06c6c;
}

.b2b-submit {
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.9rem 1rem;
  background: #ffffff;
  border: 1px solid #ffffff;
  border-radius: 0;
  color: #000000;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.b2b-submit:hover:not(:disabled) {
  background: transparent;
  color: #ffffff;
}

.b2b-submit:disabled {
  opacity: 0.6;
  cursor: default;
}

.b2b-done {
  text-align: center;
}

@media (max-width: 576px) {
  .b2b-panel {
    padding: 2rem 1.25rem;
  }
}
</style>
